import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useMultistepForm } from "./hooks/useMultistepForm";
import { useNotification } from "../../context/notification";
import { getLocalValue } from "../../utils/auth";
import { apiRequest } from "../../utils/api";
import {
  validateMandatorySchema,
  validateConditionalSchema,
  formattedUserFeedbackFormData,
  getUserFeedbackCompletedFlag,
  retrieveUserIP,
} from "./utils/index";
import { INITIAL_FORM_DATA } from "./constants/UserFeedbackFormData";
import {
  Question1CoachingMethodology,
  Question2CoachingMethodology,
  Question3CoachingMethodology,
  Question4CoachingMethodology,
  Question5FlashCoachExperience,
  Question6FlashCoachExperience,
  Question7FlashCoachExperience,
  Question8FlashCoachExperience,
  Question9FlashCoachExperience,
  Question10FlashCoachExperience,
  Question11FlashCoachExperience,
  Question12FlashCoachExperience,
  Question13FlashCoachExperience,
} from "./components/Forms/index";
import { FeedbackIntro, FeedbackIntroCTA } from "./components/Intro/index";
import FeedbackNavigationFooter from "./layout/FeedbackNavigationFooter";
export const UserFeedbackForm = () => {
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [validationError, setValidationError] = useState({ error: "" });
  const [conditionalInputError, setConditionalInputError] = useState({
    error: "",
  });
  const [timer, setTimer] = useState(0);
  const history = useHistory();
  const { addNotification } = useNotification();
  const uid = getLocalValue("uid");

  const submitFormData = async event => {
    event.preventDefault();

    try {
      const hasCompletedUserFeedback = await getUserFeedbackCompletedFlag();
      const userIP = await retrieveUserIP();
      const response = await apiRequest.post(
        `/api/user/feedback`,
        formattedUserFeedbackFormData(
          formData,
          uid,
          timer,
          userIP,
          hasCompletedUserFeedback
        )
      );
      if (response.status === 200) {
        addNotification("Your feedback was submitted successfully", "success");
        localStorage.setItem("has_completed_user_feedback", true);
        history.push("/user-feedback/feedback-success");
      }
      if (response.status === 400) {
        addNotification(
          "Validation error: All mandatory fields are not filled",
          "error"
        );
      }
    } catch (error) {
      addNotification(`Something went wrong: ${error.message}`, "error");
    }
  };

  const updateFormData = ({ inputID, ...rest }) => {
    setFormData(previousState => {
      return { ...previousState, ...rest };
    });

    setValidationError("");
  };
  const updateTextFormData = data => {
    updateFormData(data);
  };

  const updateRatingFormData = async ({ inputID, ...rest }) => {
    updateFormData({ inputID, ...rest });
    setConditionalInputError("");

    const conditionalResult = await validateConditionalInput(
      { ...rest },
      multiStepForm.currentStepIndex
    );

    if (conditionalResult?.action === "SKIP_QUESTION") {
      return multiStepForm.goForwardTwoSteps();
    }

    if (conditionalResult?.success) {
      return multiStepForm.next();
    }
    return multiStepForm.next();
  };
  const updateSelectionFormData = async data => {
    const { inputID, currentItemID, ...rest } = data;

    // look up property from state based on inputID,
    //  -> then find item.id,
    //  -> then item.checked -> !item.checked
    const updatedData = rest?.[inputID].map(item =>
      item.id === currentItemID ? { ...item, checked: !item.checked } : item
    );
    setFormData(previousState => {
      return {
        ...previousState,
        [inputID]: [...updatedData],
      };
    });
    setValidationError("");
    await validateConditionalInput(
      { [inputID]: [...updatedData] },
      multiStepForm.currentStepIndex
    );
  };

  const updateEmotionRatingFormData = async data => {
    const { inputID, rating, ...rest } = data;

    // look up property from state based on inputID,
    //  -> then find item.rating,
    //  -> then item.isSelected -> !item.isSelected
    const updatedData = rest?.[inputID].map(item => {
      if (item.isSelected || item.rating === rating) {
        return { ...item, isSelected: !item.isSelected };
      }
      return item;
    });
    setFormData(previousState => {
      return {
        ...previousState,
        [inputID]: [...updatedData],
      };
    });

    const conditionalResult = await validateConditionalInput(
      updatedData,
      multiStepForm.currentStepIndex
    );

    if (conditionalResult?.action === "SKIP_QUESTION") {
      setValidationError("");
      setConditionalInputError("");
      return multiStepForm.goForwardTwoSteps();
    }

    setValidationError("");
    setConditionalInputError("");
    return multiStepForm.next();
  };

  const onEnterKeyPress = async event => {
    if (event.key === "Enter") {
      // prevent "Enter" key from updating formData state
      event.preventDefault();
      const mandatoryResult = await validateMandatoryInput(
        formData,
        multiStepForm.currentStepIndex
      );

      if (mandatoryResult?.error) {
        return;
      }

      setValidationError("");
      setConditionalInputError("");
      return multiStepForm.next();
    }
  };

  const validateMandatoryInput = async (data, currentStepIndex) => {
    const result = await validateMandatorySchema(data, currentStepIndex);
    setValidationError({ ...result });
    return result;
  };

  const validateConditionalInput = async (data, currentStepIndex) => {
    const result = await validateConditionalSchema(data, currentStepIndex);
    setConditionalInputError({ ...result });
    return result;
  };

  const multiStepForm = useMultistepForm([
    <FeedbackIntro />,
    <Question1CoachingMethodology
      {...formData}
      updateRatingFormData={updateRatingFormData}
      validationError={validationError}
    />,
    <Question2CoachingMethodology
      {...formData}
      updateSelectionFormData={updateSelectionFormData}
      updateFormData={updateTextFormData}
      onEnterKeyPress={onEnterKeyPress}
      validationError={validationError}
    />,
    <Question3CoachingMethodology
      {...formData}
      updateFormData={updateTextFormData}
      onEnterKeyPress={onEnterKeyPress}
    />,
    <Question4CoachingMethodology
      {...formData}
      updateFormData={updateTextFormData}
      onEnterKeyPress={onEnterKeyPress}
    />,
    <Question5FlashCoachExperience
      {...formData}
      updateRatingFormData={updateRatingFormData}
    />,
    <Question6FlashCoachExperience
      {...formData}
      updateEmotionRatingFormData={updateEmotionRatingFormData}
      validationError={validationError}
    />,
    <Question7FlashCoachExperience
      {...formData}
      updateEmotionRatingFormData={updateEmotionRatingFormData}
    />,
    <Question8FlashCoachExperience
      {...formData}
      updateFormData={updateTextFormData}
      onEnterKeyPress={onEnterKeyPress}
      validationError={validationError}
    />,
    <Question9FlashCoachExperience
      {...formData}
      updateEmotionRatingFormData={updateEmotionRatingFormData}
    />,
    <Question10FlashCoachExperience
      {...formData}
      updateSelectionFormData={updateSelectionFormData}
      updateFormData={updateTextFormData}
      onEnterKeyPress={onEnterKeyPress}
      validationError={validationError}
      conditionalInputError={conditionalInputError}
    />,
    <Question11FlashCoachExperience
      {...formData}
      updateSelectionFormData={updateSelectionFormData}
      updateFormData={updateTextFormData}
      onEnterKeyPress={onEnterKeyPress}
      conditionalInputError={conditionalInputError}
    />,
    <Question12FlashCoachExperience
      {...formData}
      updateFormData={updateTextFormData}
      onEnterKeyPress={onEnterKeyPress}
    />,
    <Question13FlashCoachExperience
      {...formData}
      updateRatingFormData={updateRatingFormData}
      submitFormData={submitFormData}
    />,
  ]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimer(prev => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      {multiStepForm.currentStepComponent}
      {multiStepForm.currentStepIndex === 0 && (
        <FeedbackIntroCTA {...multiStepForm} />
      )}
      {multiStepForm.currentStepIndex > 0 && (
        <FeedbackNavigationFooter
          formData={formData}
          validationError={validationError}
          setValidationError={setValidationError}
          setConditionalInputError={setConditionalInputError}
          validateMandatoryInput={validateMandatoryInput}
          validateConditionalInput={validateConditionalInput}
          conditionalInputError={conditionalInputError}
          {...multiStepForm}
        />
      )}
    </>
  );
};
